import { useState } from 'react';
import { hubApi } from '../../lib/hubApi';
import { ConfirmModal } from './ConfirmModal';
import toast from 'react-hot-toast';

// Mirrors server/src/services/adminActions.service.ts AdminActionKind.
// Keep in sync when adding/removing kinds.
export type AdminActionKind =
  | 'freeze_user'
  | 'unfreeze_user'
  | 'deactivate_user'
  | 'reactivate_user'
  | 'reset_password'
  | 'assign_permission_set'
  | 'unassign_permission_set'
  | 'end_all_sessions'
  | 'reset_mfa';

const LABELS: Record<AdminActionKind, string> = {
  freeze_user: 'Freeze user',
  unfreeze_user: 'Unfreeze user',
  deactivate_user: 'Deactivate user',
  reactivate_user: 'Reactivate user',
  reset_password: 'Reset password',
  assign_permission_set: 'Assign permission set',
  unassign_permission_set: 'Unassign permission set',
  end_all_sessions: 'End all sessions',
  reset_mfa: 'Reset MFA',
};

const REVERSIBILITY: Record<AdminActionKind, string> = {
  freeze_user: 'Reversible. Unfreeze restores access.',
  unfreeze_user: 'Reversible. Freeze restores blocked state.',
  deactivate_user: 'Reversible. Reactivate restores the user.',
  reactivate_user: 'Reversible. Deactivate disables again.',
  reset_password: 'Sends a reset email; user must complete the flow.',
  assign_permission_set: 'Reversible. Unassign removes the grant.',
  unassign_permission_set: 'Reversible. Assign restores the grant.',
  end_all_sessions: 'The user must log in again. Cannot be undone.',
  reset_mfa: 'User must re-register MFA on next login. Cannot be undone.',
};

export type AdminActionMenuProps = {
  targetUserId: string;
  targetUserLabel?: string;
  actions: AdminActionKind[];
  permissionSetId?: string;
  findingId?: string;
  onComplete?: (kind: AdminActionKind) => void;
};

export function AdminActionMenu(props: AdminActionMenuProps) {
  const [pending, setPending] = useState<AdminActionKind | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async (kind: AdminActionKind) => {
    setBusy(true);
    try {
      // Server returns 200 on success, 502 on failure. Axios throws on 502, so
      // the catch below handles the failure toast.
      const { data } = await hubApi.post<{ status: 'success'; message?: string }>(
        '/admin-action',
        {
          kind,
          targetUserId: props.targetUserId,
          permissionSetId: props.permissionSetId,
          findingId: props.findingId,
        },
      );
      toast.success(data.message ?? `${LABELS[kind]} succeeded`);
      props.onComplete?.(kind);
    } catch (err) {
      const m = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      toast.error(m ?? (err as Error).message);
    } finally {
      setBusy(false);
      setPending(null);
    }
  };

  return (
    <>
      <div className="flex gap-1.5 text-xs">
        {props.actions.map((a) => (
          <button
            key={a}
            type="button"
            disabled={busy}
            onClick={(e) => {
              e.stopPropagation();
              setPending(a);
            }}
            className="px-2 py-0.5 rounded border border-border hover:bg-surface-3 disabled:opacity-50"
          >
            {LABELS[a]}
          </button>
        ))}
      </div>
      <ConfirmModal
        open={pending !== null}
        title={pending ? `${LABELS[pending]}?` : ''}
        body={
          <>
            Target: <strong>{props.targetUserLabel ?? props.targetUserId}</strong>
          </>
        }
        reversibilityNote={pending ? REVERSIBILITY[pending] : undefined}
        confirmLabel={pending ? LABELS[pending] : 'Confirm'}
        onCancel={() => setPending(null)}
        onConfirm={() => pending && run(pending)}
      />
    </>
  );
}
